// src/hooks/useUsers.ts
import { useState, useEffect, useCallback } from "react";
import { User } from "../types";
import { useFirestore } from "./useFirestore";

export const useUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isSearching, setIsSearching] = useState(false);

  const { getUsers, deleteUser, loading, error, searchUsers } = useFirestore();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 3 || searchTerm.length === 0) {
        setDebouncedSearchTerm(searchTerm);
        setCurrentPage(1); // Reset to first page when search changes
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load users when page or search term changes
  useEffect(() => {
    if (debouncedSearchTerm.length >= 3) {
      handleSearch();
    } else if (debouncedSearchTerm.length === 0) {
      loadUsers();
    }
  }, [currentPage, debouncedSearchTerm]);

  const loadUsers = useCallback(async () => {
    setIsSearching(false);
    try {
      const result = await getUsers(currentPage, 10);
      setUsers(result.users);
      setTotalUsers(result.total);
      setTotalPages(result.totalPages);
    } catch (err) {
      console.error("Failed to load users:", err);
    }
  }, [getUsers, currentPage]);

  const handleSearch = useCallback(async () => {
    if (debouncedSearchTerm.length >= 3) {
      setIsSearching(true);
      try {
        const result = await searchUsers(debouncedSearchTerm, currentPage, 10);
        setUsers(result.users);
        setTotalUsers(result.total);
        setTotalPages(result.totalPages);
      } catch (err) {
        console.error("Failed to search users:", err);
      } finally {
        setIsSearching(false);
      }
    } else if (debouncedSearchTerm.length === 0) {
      loadUsers();
    }
  }, [debouncedSearchTerm, currentPage, searchUsers, loadUsers]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDeleteUser = async (id: string, userName: string) => {
    if (window.confirm(`Are you sure you want to delete ${userName}?`)) {
      try {
        await deleteUser(id, userName);
        // Check if current page becomes empty and not first page
        if (users.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          if (debouncedSearchTerm.length >= 3) {
            await handleSearch();
          } else {
            await loadUsers();
          }
        }
      } catch (err) {
        console.error("Failed to delete user:", err);
      }
    }
  };

  const refreshUsers = useCallback(() => {
    if (debouncedSearchTerm.length >= 3) {
      handleSearch();
    } else {
      loadUsers();
    }
  }, [debouncedSearchTerm, handleSearch, loadUsers]);

  return {
    users,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    handleDeleteUser,
    refreshUsers,
    // Pagination
    currentPage,
    totalPages,
    totalUsers,
    handlePageChange,
    isSearching,
  };
};
